import { default as classNames, default as classnames } from 'classnames';
import { isEmpty, isEqual } from 'lodash';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CloseIcon, DeleteIcon, Edit1Icon, ErrorCircleIcon } from 'tdesign-icons-react';
import { Button, Image, Loading, MessagePlugin, RequestMethodResponse, Tooltip, TooltipLite, Upload, UploadInstanceFunctions } from 'tdesign-react';

import { ColorPickerRichMedia } from '@/components/colorPicker';
import { AddGrey, AddImage2, AI, LibrarySolid, SmallLibrary, Star3Grey, Upload as UploadIcon } from '@/components/icon';
import { BasicImageViewer } from '@/components/review/image';
import SvgIcon from '@/components/tools/svg-icon';
import { LIMIT_PIC } from '@/const/form';
import { ImageRatio, RichMediaShowType } from '@/const/richMedia';
import { UrlInfo } from '@/interface/aigc';
import { UserResourceType } from '@/interface/library';
import { EAsyncStatus, FillType, ImageDomType, RichMediaValue } from '@/interface/media';
import LibraryDialog, { ILibraryDialogRef } from '@/pages/library/dialog';
import { getColorsFromAi } from '@/services/color';

import AigcImageDialog from '../../AigcImageDialog';
import { emitRichFilterMedia, emitRichMediaPIC, getRichLocalPath, transFileInfo, transRichMediaValue } from '../change-data';
import { uploadCosImage, UploadOptions } from '../service';
import ImageDialog from './dialog';
import styles from './index.module.less';
import ImagePreviewAndEdit from './previewAndEdit';
import { getBlur } from './utils/getBlur';
import { getGrey } from './utils/getGrey';
import { getWrapInfo } from './utils/getWrap';
import { checkImageRatio, checkWH, isPicSize, isPicType } from './utils/validate';

export interface IFileInfo {
  url?: string;
  name?: string;
  width?: number;
  height?: number;
  size?: number;
  localPath?: string;
  media?: { [key: string]: any };
  sample?: { primary: string, complementary: string };
  showType?: RichMediaShowType;
  fillType?: FillType;
}

export type ImageRatioType =
  | { width: number; height: number }
  | { min: number; max: number }
  | null;

interface UploadPicProps {
  value: RichMediaValue;
  onChange: (file?: RichMediaValue, imageInfo?: any) => any;
  asyncUploadOnChange?: (file?: RichMediaValue) => any; // 剧情中卸载后处理图片loading恢复

  disabled?: boolean;
  alertRender?: React.ReactNode;
  childRender?: (info: IFileInfo) => React.ReactNode,
  btnText?: string;
  showSizeMsg?: string;

  limit?: {
    fileType?: string[];
    width?: number;
    height?: number;
    size?: number;
    showSizeMsg?: string;
    ratio?: ImageRatioType; // 宽高比
  };
  dialogConfig?: any,
  isShowAigc?: boolean;
  isImageCover?: boolean;
  domType?: ImageDomType;
  isHorizontal?: boolean;

  width?: number;
  height?: number;
  wrapStyle?: any;
  imageStyle?: any;
  contentStyle?: CSSProperties;
  normalDomStyle?: CSSProperties;

  storyStyle?: string; // 故事风格
  promptText?: string; // AI 智能默认prompt
  filterBlur?: string;
  filterGrey?: string;
  isShowMaterial?: boolean; // 展示素材库
  isCloseGetColor?: boolean; // 是否关闭获取颜色
  isShowRatioError?: boolean; // 是否展示比例错误
}

export const UploadPic: React.FC<UploadPicProps> = (props: UploadPicProps) => {
  const {
    value,
    limit = {},
    onChange,
    asyncUploadOnChange,
    width: wrapWidth = 100,
    height: wrapHeight = 100,
    contentStyle,
    isShowAigc = true,
    alertRender,
    isImageCover = false,
    domType = ImageDomType.NORMAL,
    disabled = false,
    isHorizontal = false,
    promptText = '',
    filterBlur = '',
    isShowMaterial = true,
    isCloseGetColor = false,
    isShowRatioError = false,
    // filterGrey = '',
  } = props;

  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('id') || '';
  const options: UploadOptions = { storyId };
  const filterGrey = '';
  const {
    fileType = LIMIT_PIC.type,
    size = LIMIT_PIC.size,
    width: imageWidth,
    height: imageHeight,
    ratio = ImageRatio['9:16'],
  } = limit;

  const [imageInfo, setImageInfo] = useState<IFileInfo>(transRichMediaValue(value) as IFileInfo);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [colorLoading, setColorLoading] = useState(false);
  const [isCrop, setIsCrop] = useState(false); // 是否打开裁剪工具
  const [validRatio, setValidRatio] = useState(true);

  const colorRef = useRef<any>(null);
  const uploadRef = useRef<any>(null);
  const preValueRef = useRef<RichMediaValue>({});

  useEffect(() => {
    if (asyncUploadOnChange) {
      if (value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING) {
        setUploadLoading(true);
      } else {
        setUploadLoading(false);
      }
    }
  }, [value?.asyncStatus, asyncUploadOnChange]);

  useEffect(() => {
    if (!isEqual(value, preValueRef.current)) {
      setImageInfo(transRichMediaValue(value) as IFileInfo);
      preValueRef.current = value;

      checkRatio(value?.url?.url, false);
    }
  }, [value]);

  const handleUploadImageChange = (fileArr: any) => {
    const { url, name } = fileArr[0];
    const urlInfo = { url, name };
    setImageInfo(urlInfo);
    imageValueChange(urlInfo);
  };

  const handleAsyncLoading = useCallback(() => {
    // 设置异步上传loading状态  组件卸载后，loading state 重置
    // 所以需要上层组件传值恢复组件loading状态
    asyncUploadOnChange?.({
      asyncStatus: EAsyncStatus.UPLOAD_LOADING,
    });
  }, [asyncUploadOnChange]);

  const handleAsyncInit = useCallback(() => {
    // 设置异步上传loading状态 有onChange的地方就不需要调用者，如果没有则需要调用
    asyncUploadOnChange?.({
      asyncStatus: EAsyncStatus.UPLOAD_INIT,
    });
  }, [asyncUploadOnChange]);

  const uploadFile = async (file: any): Promise<RequestMethodResponse> => {
    setUploadLoading(true);
    handleAsyncLoading();

    const res = await uploadCosImage(file.raw, options);
    if (!res?.response?.url) {
      MessagePlugin.error('上传失败,请重试');
      handleAsyncInit();
      setUploadLoading(false);
    }
    return res;
  };

  const beforeUpload = async (file: any): Promise<boolean> => {
    const validType = isPicType(file.type, fileType);
    const validSize = isPicSize(file.size, size);
    const validWH = await checkWH(file, imageWidth, imageHeight);
    return validType && validSize && validWH;
  };

  const checkRatio = async (url?: string, showMsg?: boolean) => {
    if (!url || !ratio || ratio === ImageRatio.unlimited) return;
    const isValid = await checkImageRatio(url, ratio);
    setValidRatio(isValid);

    if (!isValid && showMsg) {
      if ('min' in ratio && 'max' in ratio) {
        MessagePlugin.error(`图片比例不符合范围【${ratio.min}, ${ratio.max}】`);
      } else {
        MessagePlugin.error(`图片比例不符合${ratio.width}:${ratio.height}`);
      }

      const isGif = url?.match(/\.(gif)$/i);
      if (isGif) return;

      setIsCrop(true);
      setDialogVisible(true);
    }
  };

  const removePic = () => {
    setImageInfo(transRichMediaValue(undefined) as IFileInfo);
    onChange();
  };

  const imageValueChange = async (val?: IFileInfo, prompt?: string) => {
    const promptText = prompt === undefined ? value?.promptText : prompt;
    const result = { ...transFileInfo(val), promptText };
    const url = val?.url;

    let sample = val?.sample;
    if (url && !isCloseGetColor && (!sample?.primary || !sample?.complementary)) {
      sample = await getColorSample(url);
    }

    if (url && !isCloseGetColor && (!sample?.primary || !sample?.complementary)) {
      MessagePlugin.error('未获取到图片颜色，请重新上传');
      onChange(result);
      emitRichMediaPIC(url);
      setUploadLoading(false);
      return false;
    }

    if (url) {
      let blurUrl = {};
      let greyUrl = {};
      if (filterBlur && !filterGrey) {
        blurUrl = await getBlur(url, result, options);
        if (!isEmpty(blurUrl)) {
          emitRichFilterMedia({
            [filterBlur]: { ...blurUrl, sample },
          });
        } else {
          MessagePlugin.error('图片上传处理失败，请重新上传');
          setUploadLoading(false);
          removePic();
          return false;
        }
      }
      if (filterGrey && !filterBlur) {
        greyUrl = await getGrey(url, result, options);
        if (!isEmpty(greyUrl)) {
          emitRichFilterMedia({
            [filterGrey]: { ...greyUrl, sample },
          });
        } else {
          MessagePlugin.error('图片上传处理失败，请重新上传');
          removePic();
          setUploadLoading(false);
          return false;
        }
      }
      if (filterBlur && filterGrey && filterGrey !== filterBlur) {
        blurUrl = await getBlur(url, result, options);
        greyUrl = await getGrey(url, result, options);
        if (!isEmpty(greyUrl) && !isEmpty(blurUrl)) {
          emitRichFilterMedia({
            [filterGrey]: { ...greyUrl, sample },
            // @ts-ignore：
            [filterBlur]: { ...blurUrl, sample },
          });
        } else {
          MessagePlugin.error('图片上传处理失败，请重新上传');
          removePic();
          setUploadLoading(false);
          return false;
        }
      }
      onChange({ ...result, sample });
      emitRichMediaPIC(url);
    } else {
      onChange(result);
    }

    setImageInfo({
      ...imageInfo,
      url: url || '',
      name: val?.name || '',
      sample,
    });
    setUploadLoading(false);
    checkRatio(url, true);
  };

  const wrapInfo = getWrapInfo(wrapWidth, wrapHeight);

  const getStyle = (isInnerDialog: boolean) => {
    const innerW = isInnerDialog ? wrapInfo.width : wrapWidth;
    const innerH = isInnerDialog ? wrapInfo.height : wrapHeight;
    const horizontal = wrapInfo.type === 'horizontal';
    return { innerW, innerH, horizontal };
  };

  // AI绘图
  const [aiDialogVisible, setAiDialogVisible] = useState<boolean>(false);
  const [promptAI, setPromptAI] = useState<string>(promptText || value?.promptText || '');

  const openAiDialog = () => {
    if (disabled) return;
    setAiDialogVisible(true);
  };

  const closeAiDialog = () => setAiDialogVisible(false);

  useEffect(() => {
    setPromptAI(promptText || value?.promptText || '');
  }, [promptText, value?.promptText]);

  const handleAIconfirm = (urlInfo: UrlInfo, promptText: string) => {
    setPromptAI(promptText);
    setAiPic(urlInfo, promptText);
    closeAiDialog();
  };

  const setAiPic = async (urlInfo: UrlInfo, promptText: string) => {
    const { url, name } = urlInfo;
    if (url) {
      handleAsyncLoading();
      setUploadLoading(true);
      setImageInfo({ url, name });
      imageValueChange({ url, name: name || getRichLocalPath(url) }, promptText);
    } else {
      imageValueChange({}, promptText);
    }
  };

  // 素材库
  const imageLibraryRef = useRef<ILibraryDialogRef>(null);

  const handleLibraryConfirm = (checkValue: any) => {
    const { url, name } = checkValue;
    const urlInfo = { url, name };

    // 检查图片类型
    if (!isPicType(url || '', fileType)) {
      return;
    }

    if (url) {
      handleAsyncLoading();
      setUploadLoading(true);
      setImageInfo(urlInfo);
      imageValueChange(urlInfo);
      imageLibraryRef.current?.close();
    }
  };

  // 图片编辑弹窗
  const openImageDialog = () => {
    if (disabled) return;
    setDialogVisible(true);
  };

  const closeImageDialog = () => {
    setIsCrop(false);
    setDialogVisible(false);
  };

  const handleDialogConfirm = (imageInfo: IFileInfo) => {
    setImageInfo(imageInfo);
    imageValueChange({ ...imageInfo, showType: imageInfo.media?.showType });
    setIsCrop(false);
    setDialogVisible(false);
  };

  const handleUpload = useCallback(() => {
    (uploadRef.current as UploadInstanceFunctions)?.triggerUpload();
  }, [uploadRef]);

  const getColorSample = async (url: string) => {
    if (!url || disabled) return;

    const sample = await Promise.resolve(colorRef.current?.getColor?.(url)
      || getColorsFromAi(url).then(res => res.data));

    setColorLoading(false);
    const { primary, complementary } = sample;
    if (primary && complementary) {
      return { primary, complementary };
    }
    return undefined;
  };

  // 竖向操作按钮（上传、AI、素材库）
  const opBtnGroupVertical = (groupClass: string, style?: CSSProperties, btnClass?: string, btnTextClass?: string) => (
    <div
      className={groupClass}
      style={{ ...style }}
    >
      <Button
        theme='default'
        className={btnClass || styles.opBtnBig}
        disabled={disabled}
        onClick={handleUpload}
      >
        <div className={btnTextClass || styles.btnText}>
          <AddGrey />上传图片
        </div>
      </Button>
      {isShowAigc && <Button
        theme='default'
        className={btnClass || styles.opBtnBig}
        disabled={disabled}
        onClick={openAiDialog}
      >
        <div className={btnTextClass || styles.btnText}>
          <Star3Grey />AI 生成
        </div>
      </Button>}
      {isShowMaterial && <Button
        theme='default'
        className={btnClass || styles.opBtnBig}
        disabled={disabled}
        onClick={() => imageLibraryRef?.current?.open()}
      >
        <div className={btnTextClass || styles.btnText}>
          <LibrarySolid />素材库
        </div>
      </Button>}
    </div>
  );

  // 添加参考图样式（AI生图用）
  const getRefDom = () => (
    <Tooltip
      placement='right'
      trigger='hover'
      disabled={disabled}
      overlayStyle={{ left: '-20px' }}
      overlayInnerStyle={{ padding: 0, background: '#161718' }}
      content={<div className={styles.refTooltip}>
        <Button
          variant="text"
          className={styles.opBtnRef}
          disabled={disabled}
          onClick={handleUpload}
        >
          <div className={styles.btnRefText}>
            <AddGrey />上传图片
          </div>
        </Button>
        <Button
          variant="text"
          className={styles.opBtnRef}
          disabled={disabled}
          onClick={() => imageLibraryRef?.current?.open()}
        >
          <div className={styles.btnRefText}>
            <LibrarySolid />素材库
          </div>
        </Button>
      </div>}
    >
      <Button
        variant="text"
        loading={uploadLoading}
        disabled={disabled}
        className={styles.opBtnRef2}
      >
        {<div className={styles.btnRefText2}>
          {!uploadLoading && <AddImage2 />}
          添加参考图
        </div>}
      </Button>
    </Tooltip>
  );

  // 剧情卡内样式
  const getMissionDom = () => (
    <div className={styles.mission}>
      <Loading
        loading={uploadLoading}
        showOverlay
        size='small'
        style={{ width: '100%', height: '100%' }}
      >
        {imageInfo.url
          ? <ImagePreviewAndEdit
            value={imageInfo}
            disabled={disabled}
            colorLoading={colorLoading}
            removeIcon={<CloseIcon size={16} />}
            openImageDialog={openImageDialog}
            removePic={removePic}
          />
          : <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <Button
              theme='default'
              className={classNames(styles.opBtnMission1, {
                ['nova-disabled-opacity']: disabled,
                ['nova-disabled']: disabled,
              })}
              disabled={disabled}
              onClick={handleUpload}
            >
              <div className={styles.btnMissionText}>
                <UploadIcon style={{ width: 15, height: 15 }} />上传图片
              </div>
            </Button>
            <Button
              theme='default'
              className={classNames(styles.opBtnMission2, {
                ['nova-disabled-opacity']: disabled,
                ['nova-disabled']: disabled,
              })}
              disabled={disabled}
              onClick={openAiDialog}
            >
              <div className={styles.btnMissionText}>
                <AI style={{ width: 18, height: 18 }} />AI 生成
              </div>
            </Button>
            <Button
              theme='default'
              className={classNames(styles.opBtnMission3, {
                ['nova-disabled-opacity']: disabled,
                ['nova-disabled']: disabled,
              })}
              disabled={disabled}
              onClick={() => imageLibraryRef?.current?.open()}
            >
              <div className={styles.btnMissionText}>
                <SmallLibrary style={{ width: 18, height: 18 }} />素材库
              </div>
            </Button>
          </div>
        }
      </Loading>
    </div>
  );

  // 标准样式
  const getNormalDom = () => {
    if (props.childRender) {
      return props.childRender(imageInfo);
    }
    const { innerW, innerH, horizontal } = getStyle(false);
    const imageStyle = isImageCover
      ? horizontal ? { maxHeight: innerH } : { maxWidth: innerW }
      : { maxWidth: innerW, maxHeight: innerH };

    return (
      <div style={{ ...props.normalDomStyle }}>
        <div
          className={classnames('nova-border-box', { ['nova-disabled']: disabled })}
          style={{
            width: innerW,
            height: innerH,
            overflow: 'hidden',
            ...props.wrapStyle,
          }}
        >
          {uploadLoading && <div
            className={classnames(styles['preview-image-loading'], 'nova-box-flex')}>
            <Loading /> 上传中
          </div>}
          {imageInfo.url && !uploadLoading
            ? <div className={classnames(styles['preview-image'], 'nova-box-flex', { ['nova-disabled']: disabled })}>
              <Image
                style={{ ...imageStyle, ...props.imageStyle }}
                src={imageInfo.url}
                fit='contain'
              />
              <div className={classnames(styles['preview-image-op'], 'nova-box-flex')}
                style={{ flexDirection: 'row', gap: 4 }}>
                <Button
                  icon={<Edit1Icon size={14} />}
                  className={styles.opBtnSmall}
                  variant="text"
                  onClick={openImageDialog}
                  disabled={colorLoading || disabled}
                />
                <div className={styles.review}>
                  <BasicImageViewer hover={false} images={[imageInfo.url]} />
                </div>
                <Button
                  icon={<DeleteIcon size={14} />}
                  className={styles.opBtnSmall}
                  variant="text"
                  disabled={disabled}
                  onClick={removePic}
                />
              </div>
              {isShowRatioError && !validRatio
                && <TooltipLite content={'图片比例错误'}>
                  <ErrorCircleIcon className={styles.ratioError} />
                </TooltipLite>}
            </div>
            : <div className={classnames(styles['preview-image'], 'nova-box-flex', { ['nova-disabled']: disabled })}>
              <div className={classnames(styles['preview-image-add'], 'nova-box-flex')}>
                <SvgIcon name="add" width="20" height="20" />
                <div style={{ position: 'absolute', top: 'calc(50% + 8px)' }}>{props.btnText}</div>
              </div>
              {opBtnGroupVertical(classnames(styles['preview-image-op'], 'nova-box-flex'))}
            </div>
          }
        </div>
        {alertRender && <div className='nova-text-tips'
          style={{ width: innerW }}>
          {alertRender}
        </div>}
      </div>
    );
  };

  return (
    <div style={{ ...contentStyle }} className={styles.container}>
      {(function renderDomType() {
        switch (domType) {
          case ImageDomType.MISSION:
            return getMissionDom();
          case ImageDomType.REF:
            return getRefDom();
          default:
            return getNormalDom();
        }
      }())}

      {/* 图片编辑弹窗 */}
      <ImageDialog
        config={props.dialogConfig}
        visible={dialogVisible}
        imageInfo={imageInfo}
        ratio={ratio}
        isCrop={isCrop}
        disabled={disabled}
        isHorizontal={isHorizontal}
        fileType={fileType}
        isCloseGetColor={isCloseGetColor}
        onClose={closeImageDialog}
        onConfirm={handleDialogConfirm}
      />

      {/* AI 生成弹窗 */}
      <AigcImageDialog
        visible={aiDialogVisible}
        onClose={closeAiDialog}
        onConfirm={handleAIconfirm}
        data={{
          promptText: promptAI,
          imageUrl: imageInfo.url || '',
          style: props.storyStyle,
        }}
        isHorizontal={isHorizontal}
      />

      {/* 素材库弹窗 */}
      <LibraryDialog
        dialogRef={imageLibraryRef}
        type={UserResourceType.RESOURCE_TYPE_IMAGE}
        confirmCallback={handleLibraryConfirm}
      />

      {/* 上传组件 */}
      <Upload
        ref={uploadRef}
        style={{ display: 'none' }}
        disabled={disabled}
        requestMethod={uploadFile}
        onChange={handleUploadImageChange}
        beforeUpload={beforeUpload}
        allowUploadDuplicateFile={true}
        files={[{
          url: imageInfo.url,
          name: imageInfo.name,
        }]}
      />

      {/* 取色组件 */}
      <ColorPickerRichMedia
        colorRef={colorRef}
        value={imageInfo.sample}
        url={imageInfo.url || ''}
        isShowColorPicker={false}
        getColorStatus={v => setColorLoading(v)}
        miniPicker={true}
        disabled={disabled}
      />
    </div>
  );
};

export default UploadPic;
